import { Role } from 'auth';
import Big from 'big.js';
import { FormikProvider, useFormik } from 'formik';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { createSearchParams, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import * as yup from 'yup';
import { SignUpPagesWrapper } from '@/modules/auth/components/SignUpPagesWrapper.tsx';
import { withPrivateAccess } from '@/modules/auth/hocs/withPrivateAccess.tsx';
import CurrencyInput from '@/modules/common/components/CurrencyInput.tsx';
import UploadDocumentsBlock from '@/modules/common/components/UploadDocumentsBlock.tsx';
import { BigRoundMode } from '@/modules/common/constants/big-round-mode.ts';
import {
  APPLY_REGISTER_CLASS_ID,
  APPLY_REGISTER_ID,
  DATE_RANGE_SEARCH_PARAM_KEY,
  ENTITY_ID_SEARCH_PARAM_KEY,
  REGISTER_ID_SEARCH_PARAM_KEY,
  SUBSCRIBE_FROM_PARAM_KEY,
} from '@/modules/common/constants/search-params.ts';
import { useGetApplyRegisterName } from '@/modules/common/hooks/useGetApplyRegisterName.ts';
import { registerTypeStrings } from '@/modules/common/hooks/useRegisterTypeStrings.tsx';
import { entityPaths } from '@/modules/entity/routes.tsx';
import { useMyActivitiesListQuery } from '@/modules/investor-dashboard/queries/activity.tsx';
import { useGetRegisterClassItem } from '@/modules/investor-dashboard/queries/registers.tsx';
import {
  useCreateTransactionMutation,
  useCurrentEntityShareholdersQuery,
} from '@/modules/investor-dashboard/queries/transactions.tsx';
import { investorDashboardPaths } from '@/modules/investor-dashboard/routes.tsx';
import { dividendReinvestmentOptions } from '@/modules/issuer-dashboard/components/modals/IssuerAddSubscriptionModal.tsx';
import { Alert, Button, CustomSwitch, SelectButtons, Skeleton } from 'ui';
import { AlertBlueIcon } from 'ui/icons';
import { goodFormatUSDCurrency } from 'ui/utils';
import { ActivitiesType, CreateTransactionRequest, TransactionType } from 'api';

const validationSchema = yup.object({
  register_class_id: yup.number(),
  shareholder_id: yup.number().required('Shareholder is required'),
  type: yup.string().trim().required(),
  quantity: yup.number(),
  price_per_share: yup.number(),
  memo: yup.string().trim(),
  dividend_reinvestment: yup.boolean(),
  dividend_event_id: yup.number().nullable(),
});

const initialValues = {
  register_class_id: 0,
  shareholder_id: undefined,
  type: TransactionType.Subscription,
  quantity: 1,
  price_per_share: 0,
  memo: '',
  dividend_reinvestment: true,
  dividend_event_id: undefined,
  total: '',
};

const SubscribePage = () => {
  const [isFileLoadMode, setIsFileLoadMode] = useState(false);
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { id: entity_id = 0 } = useParams();
  const apply_register_class_id = Number(params.get(APPLY_REGISTER_CLASS_ID));
  const apply_register_id = Number(params.get(APPLY_REGISTER_ID));
  const subscribe_from = params.get(SUBSCRIBE_FROM_PARAM_KEY);
  const auth = useAuth();

  const {
    name: applyRegisterName,
    isLoading: isApplyRegisterLoading,
    type: applyRegisterType,
    isError: isErrorRegister,
  } = useGetApplyRegisterName();

  const {
    data: registerClassData,
    isLoading: isRegisterClassDataLoading,
    isError: isErrorRegisterClass,
  } = useGetRegisterClassItem(
    apply_register_class_id,
    !apply_register_class_id || isNaN(apply_register_class_id),
  );

  const { data: applyClassTransactionsData } = useMyActivitiesListQuery(
    {
      page: 1,
      page_size: 1,
      register_class_id: apply_register_class_id,
      type: ActivitiesType.Subscription,
    },
    +apply_register_id,
    !apply_register_class_id ||
      !apply_register_id ||
      isNaN(apply_register_class_id) ||
      isNaN(apply_register_id) ||
      !entity_id,
    +entity_id,
  );

  const { data: entityShareholdersResponse, isLoading: isLoadingShareholders } =
    useCurrentEntityShareholdersQuery(Number(entity_id), !entity_id || +entity_id < 1);

  const isAllowFractionalShares = useMemo(() => {
    return (
      (entityShareholdersResponse?.shareholders || [])?.find(
        ({ register_id }) => register_id === apply_register_id,
      )?.register_obj?.allows_fractional_shares || false
    );
  }, [apply_register_id, entityShareholdersResponse?.shareholders]);

  const minInvestment = useMemo(() => {
    const min_investment = registerClassData?.register_class?.min_investment;
    const current_price_per_share = registerClassData?.register_class?.current_price_per_share;

    if (isAllowFractionalShares || !min_investment || !current_price_per_share) {
      return min_investment || current_price_per_share || 1;
    }

    const sharesPerMinimalPrice = min_investment / current_price_per_share;

    return Math.ceil(sharesPerMinimalPrice) * current_price_per_share;
  }, [isAllowFractionalShares, registerClassData]);

  const getInitialValues = () => {
    return {
      ...initialValues,
      register_class_id: apply_register_class_id,
    } as unknown as CreateTransactionRequest & { total: number };
  };

  const formik = useFormik<CreateTransactionRequest & { total: number }>({
    initialValues: getInitialValues(),
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    validationSchema: validationSchema.shape({
      total: yup.number().nullable(),
    }),
    onSubmit: async (payload, { setErrors }) => {
      const total = Number(payload.total);

      if (minInvestment > total) {
        setErrors({ total: 'Minimum subscription amount is AU $' + minInvestment });
        return;
      }

      await mutateAsync({
        ...payload,
        register_id: apply_register_id,
        shareholder_id: shareholderId || payload.shareholder_id,
      });
      await auth.signinSilent();
    },
  });
  const { setFieldValue } = formik;

  const register_id = useMemo(
    () => (entityShareholdersResponse?.shareholders || [])?.[0]?.register_id || 'all',
    [entityShareholdersResponse?.shareholders],
  );
  const { mutateAsync, isLoading: isSubscriptionCreating } = useCreateTransactionMutation({
    setErrors: formik.setErrors,
    isSubscriptionAfterEntityCreate: true,
    onSuccess: () => {
      if (subscribe_from === 'list') {
        handleGoDashboard();
      }
      if (subscribe_from === 'create') {
        handleGoDashboard();
      }
    },
  });

  const handleChangeReinvestment = (reinvestment: boolean) => {
    formik.setFieldValue('dividend_reinvestment', reinvestment);
  };

  const handleChangeTotal = (total: number | string) => {
    formik.setFieldValue('total', total);
    const quantity = total
      ? Number(
          new Big(total)
            .div(formik.values.price_per_share || 1)
            .round(2, BigRoundMode.roundHalfUp)
            .valueOf(),
        )
      : total;
    formik.setFieldValue('quantity', !isAllowFractionalShares ? Math.floor(+quantity) : quantity);
    formik.setFieldError('total', '');
  };

  const shareholderId = useMemo(() => {
    const shareholderIdByRegisterId = (entityShareholdersResponse?.shareholders || []).find(
      item => item.register_id === apply_register_id,
    )?.id;

    if (shareholderIdByRegisterId) {
      setFieldValue('shareholder_id', shareholderIdByRegisterId);
    }
    return shareholderIdByRegisterId;
  }, [apply_register_id, entityShareholdersResponse?.shareholders, setFieldValue]);

  useEffect(() => {
    if (registerClassData?.register_class?.current_price_per_share) {
      setFieldValue('price_per_share', registerClassData?.register_class?.current_price_per_share);
    }
  }, [registerClassData, setFieldValue]);

  const handleGoBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleGoToEntitiesList = useCallback(() => {
    navigate(
      { pathname: entityPaths.static.entityList, search: params.toString() },
      {
        replace: false,
      },
    );
  }, [navigate, params]);

  const handleGoDashboard = useCallback(() => {
    navigate(
      {
        pathname: investorDashboardPaths.static.dashboard,
        search: createSearchParams({
          ...(params.get('entity_id') === String(entity_id)
            ? Object.fromEntries(params.entries())
            : {}),
          [ENTITY_ID_SEARCH_PARAM_KEY]: String(entity_id),
          [REGISTER_ID_SEARCH_PARAM_KEY]: apply_register_id
            ? String(apply_register_id)
            : String(register_id),
          [DATE_RANGE_SEARCH_PARAM_KEY]: 'all',
          [APPLY_REGISTER_ID]: String(apply_register_id ?? ''),
          [APPLY_REGISTER_CLASS_ID]: String(apply_register_class_id ?? ''),
        }).toString(),
      },
      {
        replace: false,
      },
    );
  }, [apply_register_class_id, apply_register_id, entity_id, navigate, params, register_id]);

  const handleSkip = useCallback(() => {
    handleGoDashboard();
  }, [handleGoDashboard]);

  const title =
    isApplyRegisterLoading || isRegisterClassDataLoading ? (
      <div>
        <Skeleton width={280} height={50} className="mb-5" />
        <Skeleton width={280} height={50} className="mb-10" />
      </div>
    ) : (
      <span className="text-3xl md:text-5xl md:leading-[56px] font-semibold font-serif">
        <span>You have been invited to subscribe for class</span>{' '}
        <span className="text-blue-700">
          {(registerClassData?.register_class?.class_name || '').replace(/(shares|units)$/, '')}{' '}
        </span>
        <span className="text-blue-700">
          {applyRegisterType && registerTypeStrings[applyRegisterType].share + 's'}
        </span>
        <span> of </span>
        <span> {applyRegisterType} </span>
        <span className="text-blue-700">{applyRegisterName?.replace(/(fund|company)/i, '')}</span>
      </span>
    );

  const handleChangeDocuments = useCallback(
    (filesData: string[]) => {
      formik.setFieldValue('document_data', filesData);
      formik.setFieldError('document_data', '');
    },
    [formik],
  );

  return (
    <SignUpPagesWrapper
      backAction={{ onClick: handleGoBack }}
      mainAction={{ name: 'Back to entities list', onClick: handleGoToEntitiesList }}
    >
      <FormikProvider value={formik}>
        <div className="w-full flex h-full items-center justify-center">
          <div className="max-w-[660px] w-full px-3 sm:px-6 flex-col items-start justify-center">
            {title}
            {registerClassData && (
              <Alert
                className="mt-10"
                variant="neutral-2"
                icon={AlertBlueIcon}
                label={
                  'Please note that the minimum subscription amount is AU ' +
                  goodFormatUSDCurrency(minInvestment)
                }
              />
            )}
            <CurrencyInput
              error={formik.errors.total}
              name="total"
              label="Amount you wish to invest"
              placeholder="Investemnt amount..."
              value={formik.values.total}
              onChange={handleChangeTotal}
            />
            <SelectButtons<boolean>
              label="REINVESTMENT DISTRIBUTION ELECTION"
              options={dividendReinvestmentOptions}
              selected={dividendReinvestmentOptions.find(
                ({ value }) => value === formik.values.dividend_reinvestment,
              )}
              onChange={handleChangeReinvestment}
              className="mt-6"
            />
            {formik.errors.shareholder_id && (
              <span className="text-sm text-[12px] text-error-red">
                {formik.errors.shareholder_id}
              </span>
            )}
            <CustomSwitch
              label="Do you want to upload an application document?"
              value={isFileLoadMode}
              className="mt-4 text-sm"
              onChange={setIsFileLoadMode}
            />
            {isFileLoadMode && (
              <UploadDocumentsBlock
                classes={{ label: 'mt-4' }}
                label="Upload document JPG, PNG or PDF. Up to 10 mb (Optional)"
                error={formik.errors.document_data}
                onChange={handleChangeDocuments}
                value={formik.values.document_data || []}
              />
            )}
            <Button
              className="mt-10"
              type="submit"
              disabled={
                isLoadingShareholders ||
                isSubscriptionCreating ||
                isRegisterClassDataLoading ||
                isErrorRegister ||
                isErrorRegisterClass ||
                isApplyRegisterLoading ||
                !entityShareholdersResponse?.shareholders?.length
              }
              onClick={formik.handleSubmit}
              loading={isSubscriptionCreating}
            >
              Subscribe
            </Button>
            {(applyClassTransactionsData?.pages?.[0].total ?? 0) > 0 && (
              <Button className="mt-3" variant="secondary" onClick={handleSkip}>
                Skip
              </Button>
            )}
          </div>
        </div>
      </FormikProvider>
    </SignUpPagesWrapper>
  );
};

export default withPrivateAccess(SubscribePage, Role.SR_INVESTOR);
